import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_POWDER, GRAY_LIGHTER, GRAY_LIGHT} from 'ui-library/Colors';
import {GRAY} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  FieldUISchemaWithStandardComponent,
  isFieldUISchemaWithStandardComponent,
  CustomComponentEditorProps,
  ShapeUISchema,
  ConfigShapePath,
} from '../StepConfigEditorTypes';
import ColumnReferenceEditor from './ColumnReferenceEditor';
import SectionName from './SectionName';
import StepConfigEditorRouter from './StepConfigEditorRouter';
import StepConfigFieldsTemplate, {StepConfigField, VariableHeaderOrNull} from './StepConfigFieldsTemplate';

const isCollapsedGlobalState = makeUseGlobalClientState<boolean>(false);

/**
 * @package
 *
 * We need a custom component here because the icon needs to grow vertically if the column name takes up
 * multiple lines.
 */
export function IconButton({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}): JSX.Element {
  return (
    <div
      className={css(
        styles.icon,
        isCollapsed && styles.iconCollapsed,
        !isCollapsed && styles.iconNotCollapsed
      )}
      onClick={() => {
        setIsCollapsed(!isCollapsed);
      }}
    >
      <BenchlingIcon icon="filter" />
    </div>
  );
}

/**
 *
 * Create a column editor that sits above the collapsible section.
 *
 * We are not using StepConfigEditorRouter here because of some
 * styling needs (rounded corners, no bottom margin). This component
 * is outside of the StepConfigFieldsTemplate because we want the
 * column and the button to be the same width as the collapsing section.
 *
 * @package
 */
export function ColumnEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
  setIsCollapsed,
  isCollapsed,
  columnReferenceUiSchema,
}: {
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  configShape: PipelineConfigJsonSpec;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
  columnReferenceUiSchema: FieldUISchemaWithStandardComponent;
}): JSX.Element {
  assert(
    isFieldUISchemaWithStandardComponent(columnReferenceUiSchema),
    'First field must be a custom component'
  );
  const currentConfigShape = getConfigShapeAtConfigShapePath(
    columnReferenceUiSchema.configShapePath,
    configShape,
    config
  );
  assert(currentConfigShape.kind === 'columnReference', 'First field must be a column reference');

  return (
    <div className={css(styles.firstRowWrapper)}>
      <div className={css(styles.firstRow)}>
        <div className={css(styles.grow)}>
          <ColumnReferenceEditor
            config={maybeGetValueFromConfig(config, columnReferenceUiSchema.configShapePath) as string | null}
            configShape={currentConfigShape}
            onChangeConfig={(newValue) => {
              onChangeConfig(config, columnReferenceUiSchema.configShapePath, newValue);
            }}
            disabled={disabled}
            buttonSize={ButtonSize.SMALL}
            cornerRadius={
              isCollapsed
                ? {topLeft: 4, bottomLeft: 4, topRight: 0, bottomRight: 0}
                : {topLeft: 4, bottomLeft: 0, topRight: 0, bottomRight: 0}
            }
          />
        </div>

        <IconButton isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      </div>
    </div>
  );
}

/** @package */
export function CollapsingSection({
  collapsibleFields,
  rangeMinFieldIndex,
  rangeMaxFieldIndex,
}: {
  collapsibleFields: Array<StepConfigField>;
  rangeMinFieldIndex: number;
  rangeMaxFieldIndex: number;
}): JSX.Element {
  const beforeRangeFields = collapsibleFields.slice(0, rangeMinFieldIndex);
  const rangeFields = collapsibleFields.slice(rangeMinFieldIndex, rangeMaxFieldIndex + 1);
  const afterRangeFields = collapsibleFields.slice(rangeMaxFieldIndex + 1);

  return (
    <div className={css(styles.collapsedSection)}>
      {beforeRangeFields.length > 0 && (
        <StepConfigFieldsTemplate numColumns={1} fields={beforeRangeFields} isCompact={true} />
      )}
      <StepConfigFieldsTemplate numColumns={2} fields={rangeFields} isCompact={true} />
      {afterRangeFields.length > 0 && (
        <StepConfigFieldsTemplate numColumns={1} fields={afterRangeFields} isCompact={true} />
      )}
    </div>
  );
}

export interface CollapsibleColumnSectionEditorProps extends CustomComponentEditorProps {
  uiSchema: ShapeUISchema;
  rangeMinSystemName: string;
  rangeMaxSystemName: string;
}

export default function CollapsibleColumnSectionEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
  uiSchema,
  rangeMinSystemName,
  rangeMaxSystemName,
}: CollapsibleColumnSectionEditorProps): JSX.Element {
  const {clientStateId} = useStepConfigEditorContext();
  const [isCollapsed, setIsCollapsed] = isCollapsedGlobalState.useGlobalClientState(
    `${uiSchema[SECTION_NAME_UI_SCHEMA_KEY]}-${clientStateId}`
  );
  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const firstFieldUiSchema = uiSchema[fieldOrder[0]];
  assert(
    isFieldUISchemaWithStandardComponent(firstFieldUiSchema),
    'First field must be a standard component'
  );

  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
  const fieldOrderWithoutColumnReference = fieldOrder.slice(1);
  const rangeMinFieldIndex = fieldOrderWithoutColumnReference.findIndex(
    (variableName) => variableName === rangeMinSystemName
  );
  const rangeMaxFieldIndex = fieldOrderWithoutColumnReference.findIndex(
    (variableName) => variableName === rangeMaxSystemName
  );
  const collapsibleFields = fieldOrderWithoutColumnReference.map((variableName) => {
    const field = uiSchema[variableName];
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);
    assert(isFieldUISchemaWithStandardComponent(field), 'Field must be a standard component');
    return {
      variableName: field.displayName,
      infoText: field.infoText,
      editor: (
        <StepConfigEditorRouter
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={onChangeConfig}
          disabled={disabled}
          uiSchema={uiSchema[variableName]}
        />
      ),
    };
  });

  return (
    <Row>
      <Col sm={12}>
        <SectionName sectionName={sectionName} isCompact={true} />
      </Col>
      <Col sm={12}>
        <VariableHeaderOrNull
          variableName={firstFieldUiSchema?.displayName}
          infoText={firstFieldUiSchema?.infoText}
          isCompact={true}
        />
      </Col>
      <Col sm={12}>
        <ColumnEditor
          config={config}
          onChangeConfig={onChangeConfig}
          disabled={disabled}
          configShape={configShape}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          columnReferenceUiSchema={firstFieldUiSchema}
        />
      </Col>
      {!isCollapsed && (
        <Col sm={12}>
          <CollapsingSection
            collapsibleFields={collapsibleFields}
            rangeMinFieldIndex={rangeMinFieldIndex}
            rangeMaxFieldIndex={rangeMaxFieldIndex}
          />
        </Col>
      )}
    </Row>
  );
}

const styles = StyleSheet.create({
  collapsedSection: {
    borderWidth: '0 1px 1px 1px',
    borderStyle: 'solid',
    borderColor: GRAY,
    backgroundColor: GRAY_LIGHTER,
    borderRadius: '0 0 4px  4px',
    marginBottom: 10,
    paddingTop: 4,
  },
  firstRow: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  firstRowWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  grow: {
    flexGrow: 1,
  },
  icon: {
    width: 28,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: '1px 1px 1px 0',
    borderStyle: 'solid',
    borderColor: GRAY,
  },
  iconNotCollapsed: {
    borderRadius: '0 4px 0 0',
    color: BLUE_LIGHT,
    backgroundColor: BLUE_POWDER,
  },
  iconCollapsed: {
    borderRadius: '0 4px 4px 0',
    backgroundColor: GRAY_LIGHT,
  },
});
